import { useRef } from 'react';
import { theme } from 'antd';
import DataTableEcho from '@/components/tables/DataTable/DataTableEcho';
import TableColumnsEcho from './TableColumnsEcho';
import TableContentEcho from './TableContentEcho';
import type { DateRange, SortField, TableContainerProps, ShowFilter } from '@/types';
import { mixpanelTrack } from '@/utils/mixpanelUtils/asyncMixpanel';
import { MIXPANEL_EVENT, RevenueDashboardWidget } from '@/utils/mixpanelUtils/events';
import useCampaignTypesModalData from '@/components/modals/CampaignTypesModal/useCampaignTypesModalData';
import useBreakpoint from '@/hooks/useBreakpoint';
import { debounce } from 'lodash-es';
import { buildCampaignsEchoColumns } from '@/components/tables/CampaignsTable/buildCampaignsEchoColumns';

/**
 * This component is used to display the campaign types table.
 * @param visibleCategories - The visible categories for the campaign types modal.
 * @param setVisibleCategories - The function to set the visible categories for the campaign types modal.
 * @param modalDateRange - The date range for the campaign types modal.
 * @returns The table component.
 */
const CampaignTypesTableEcho = ({
  visibleCategories,
  setVisibleCategories,
  modalDateRange,
  modalSelectedShow,
}: TableContainerProps & {
  modalDateRange?: DateRange;
  modalSelectedShow?: ShowFilter;
}) => {
  const { data, isLoading, pageType } = useCampaignTypesModalData(
    modalDateRange,
    modalSelectedShow
  );
  const { token } = theme.useToken();
  const { isMobile } = useBreakpoint();

  const columnSortEventName =
    pageType === 'earnings'
      ? MIXPANEL_EVENT.earnings.campaignTypes.tableSortColumnClicked
      : MIXPANEL_EVENT.payouts.campaignTypes.tableSortColumnClicked;

  const debouncedTrackSort = useRef(
    debounce(
      (sortField: SortField) =>
        mixpanelTrack(columnSortEventName, {
          widget: RevenueDashboardWidget.CampaignTypes,
          page: pageType,
          column: sortField.key,
          order: sortField.direction,
        }),
      1000
    )
  );

  // Refs for tooltips
  const impressionsHeaderRef = useRef<HTMLDivElement>(null);
  const brandedContentRef = useRef<HTMLDivElement>(null);
  const socialRef = useRef<HTMLDivElement>(null);
  const otherRef = useRef<HTMLDivElement>(null);

  const handleSort = (sortField: SortField) => {
    debouncedTrackSort.current(sortField);
  };

  return (
    <DataTableEcho
      columns={buildCampaignsEchoColumns(
        TableColumnsEcho({
          popoverRef: impressionsHeaderRef,
          visibleCategories: visibleCategories!,
          setVisibleCategories: setVisibleCategories!,
          pageType,
          token,
          isMobile,
        }),
        token
      )}
      dataSource={TableContentEcho(
        data,
        pageType,
        visibleCategories!,
        setVisibleCategories!,
        {
          brandedContent: brandedContentRef,
          social: socialRef,
          other: otherRef,
        },
        token
      )}
      isLoading={isLoading}
      dataTestId="campaign-types-table"
      onSort={handleSort}
    />
  );
};

export default CampaignTypesTableEcho;
